import React, { useEffect, useContext } from 'react';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { connect } from 'react-redux';
import { useConsent } from '@carvana/core-consent';
import { personalDetailsSelectors } from '../../../PersonalDetails/ducks';
import Toggle from '../Toggle';
import { notificationSettingsOperations, notificationSettingsSelectors } from '../../ducks';
import { formatPhoneNumber } from '../../../../utilities';
import {
  NotificationsWrapper,
  HeaderAlt,
  TitleWrapper,
  Title,
  Subtitle,
  EditLink,
  Description,
  BottomBorder,
  SubDescription
} from '../ShowNotificationSettings.styles';
import { sharedSelectors, sharedOperations } from '../../../../shared/ducks';
import { NotificationSettingsFeatureFlags } from '../../../../contracts';
import type { RootState } from '../../../../store/types';

type OwnProps = {
  trackEventable: (message: boolean, customAttributes: Record<string, string>) => void;
};

type StateProps = {
  isSubscribedToTCPA: boolean;
  isUpdatingTCPANotifications: boolean;
  mayEditPersonalDetails: boolean;
  notificationSettingsFeatureFlags: NotificationSettingsFeatureFlags;
  phoneNumber: string;
};

type DispatchProps = {
  navigateToAccountPage: (url: string) => void;
  updateTCPANotifications: (value: boolean, featureFlags: NotificationSettingsFeatureFlags) => void;
};

function PhoneNotifications({
  trackEventable,
  phoneNumber,
  isSubscribedToTCPA,
  updateTCPANotifications,
  isUpdatingTCPANotifications,
  mayEditPersonalDetails,
  notificationSettingsFeatureFlags,
  navigateToAccountPage
}: OwnProps & StateProps & DispatchProps) {
  const { shouldShowSms, shouldShowPhoneCall } = notificationSettingsFeatureFlags;
  const shouldShowPhoneNotifications = shouldShowSms || shouldShowPhoneCall;
  if (!shouldShowPhoneNotifications) return null;

  const { consentHtml, triggerConsentEvents } = useConsent();
  const { trackEvent } = useContext(AnalyticsContext);

  useEffect(() => {
    triggerConsentEvents('Tcpa', (eventData) => {
      trackEvent(
        `user platform - account - phone notifications - displayed default tcpa`,
        eventData
      );
    });
  }, []);

  const handleEditLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const personalInformationUrl = '/personal-details/personal-information';
    navigateToAccountPage(personalInformationUrl);
  };

  return (
    <NotificationsWrapper data-testid="phone-notification-section">
      <HeaderAlt>
        <TitleWrapper>
          <Title>Phone Notifications</Title>
          {phoneNumber && (
            <Subtitle className="has-bottom-margin" data-qa="phone-subtitle">
              {formatPhoneNumber(phoneNumber)}
              {mayEditPersonalDetails && (
                <EditLink data-qa="phone-edit-link" href="" onClick={handleEditLinkClick}>
                  {' '}
                  EDIT
                </EditLink>
              )}
            </Subtitle>
          )}
        </TitleWrapper>
        <Toggle
          dataQa="phone-toggle"
          value={isSubscribedToTCPA}
          onClick={() => {
            trackEventable(!isSubscribedToTCPA, { consentType: 'phone-tcpa' });
            updateTCPANotifications(!isSubscribedToTCPA, notificationSettingsFeatureFlags);

            triggerConsentEvents('Tcpa', (eventData) => {
              trackEvent(
                `user platform - account - phone notifications - submitted default tcpa`,
                eventData
              );
            });
          }}
          disabled={isUpdatingTCPANotifications}
        />
      </HeaderAlt>
      <Description className="title-description">
        Looking for special offers, real-time price updates and vehicle alerts? Opt-in and get these
        notifications straight to your phone!
      </Description>
      <SubDescription dangerouslySetInnerHTML={{ __html: consentHtml?.Tcpa?.html }} />
      <BottomBorder className="last-border" />
    </NotificationsWrapper>
  );
}

const mapDispatchToProps = {
  updateTCPANotifications: notificationSettingsOperations.updateTCPANotifications,
  navigateToAccountPage: sharedOperations.navigateToAccountPage
};

const mapStateToProps = (state: RootState) => ({
  phoneNumber: personalDetailsSelectors.getPhoneNumber(state),
  isSubscribedToTCPA: notificationSettingsSelectors.getIsSubscribedToTCPA(state),
  isUpdatingTCPANotifications: notificationSettingsSelectors.getIsUpdatingTCPANotifications(state),
  mayEditPersonalDetails: personalDetailsSelectors.getMayEditPersonalDetails(state),
  notificationSettingsFeatureFlags: sharedSelectors.getNotificationSettingsFeatureFlags(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(PhoneNotifications);
